import { useEffect, useRef, useState, type RefObject } from 'react';

export interface UseDrawerDragOptions {
  setExpanded: (expanded: boolean) => void;
  isExpanded: boolean;
  onClose: () => void;
  open: boolean;
}

export interface UseDrawerDragReturn {
  dragRef: RefObject<HTMLDivElement>;
  delta: number | null;
  isDragging: boolean;
}

const EXPAND_TRESHOLD = 30;
const MINIMAL_MOVE_TRESHOLD = 4;

export const useDrawerDrag = ({
  isExpanded,
  setExpanded,
  onClose,
  open,
}: UseDrawerDragOptions): UseDrawerDragReturn => {
  const [delta, setDelta] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const originRef = useRef<number | null>(null);
  const deltaRef = useRef<number | null>(null);
  const hasMovedRef = useRef(false);
  const dragRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = dragRef.current;
    if (!element) return;

    const handlePointerDown = (event: PointerEvent): void => {
      originRef.current = event.clientY;
      setDelta(0);
      deltaRef.current = 0;
      setIsDragging(true);

      document.addEventListener('pointermove', handleMove, { passive: true });
      document.addEventListener('pointerup', handlePointerUp, { passive: true });
    };

    const handleMove = (event: PointerEvent): void => {
      if (originRef.current === null) return;
      const newDelta = originRef.current - event.clientY;

      if (isExpanded && newDelta > 0) {
        setDelta(0);
      } else if (!isExpanded && newDelta < 0) {
        setDelta(newDelta);
      } else {
        setDelta(newDelta);
      }

      deltaRef.current = newDelta;
      hasMovedRef.current = true;
    };

    const handlePointerUp = (): void => {
      const deltaValue = deltaRef.current;

      if (deltaValue == null) return;
      if ((isExpanded && deltaValue < -EXPAND_TRESHOLD) || (!isExpanded && deltaValue > EXPAND_TRESHOLD)) {
        setDelta(null);
        setExpanded(deltaValue > 0);
      } else if (!isExpanded && deltaValue < -EXPAND_TRESHOLD) {
        onClose();
      } else if (!hasMovedRef.current || Math.abs(deltaValue) < MINIMAL_MOVE_TRESHOLD) {
        setExpanded(!isExpanded);
        setDelta(null);
      } else {
        setDelta(null);
      }

      setIsDragging(false);
      originRef.current = null;
      deltaRef.current = null;
      hasMovedRef.current = false;

      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    const handleTouchStart = (event: TouchEvent): void => {
      event.stopPropagation();
      event.preventDefault();
    };

    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('pointerdown', handlePointerDown);

    return (): void => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isExpanded, onClose, setExpanded]);

  useEffect(() => {
    if (open) setDelta(null);
  }, [open]);

  return {
    dragRef,
    delta,
    isDragging,
  };
};
